import dayjs from "dayjs";
import { Icon, Text } from "@utils/helper";
import { useState, forwardRef, useEffect, useRef } from "react";
import useTranslation from "next-translate/useTranslation";
import { FormControl, FormHelperText } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";

import "dayjs/locale/ar";
import "dayjs/locale/en";

const DateComponent = forwardRef(
  (
    {
      customLabel,
      classNameLabel,
      clsName,
      name,
      sx = {},
      className,
      helperText,
      placeholder,
      error,
      inputBg = "#f8fbff",
      ...otherProps
    },
    ref
  ) => {
    const { lang } = useTranslation();

    const [hasValue, setHasValue] = useState(dayjs(otherProps.value).isValid());
    const [isValidDate, setIsValidDate] = useState(false);

    useEffect(() => {
      dayjs.locale(lang === "ar" ? "ar" : "en");
    }, [lang]);

    const isMouseDown = useRef(false);

    useEffect(() => {
      const handleMouseUp = () => {
        isMouseDown.current = false;
      };

      window.addEventListener("mouseup", handleMouseUp);

      return () => {
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }, []);

    useEffect(() => {
      const parsedDate = dayjs(otherProps.value, "DD-MM-YYYY", true);
      const isValidDate = parsedDate.isValid();
      setIsValidDate(isValidDate);
    }, [otherProps.value]);

    return (
      <div className={`flex flex-col gap-3 w-full ${clsName}`}>
        {customLabel && (
          <Text fontType="Expo_sim" weight={500} className={classNameLabel}>
            {customLabel}
          </Text>
        )}
        <div className="flex w-full gap-3">
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={lang}>
            <FormControl
              error={error}
              component="fieldset"
              className={className || "block w-full"}
            >
              <div
                onMouseDown={() => {
                  isMouseDown.current = true;
                }}
                onFocus={(e) => {
                  if (isMouseDown.current) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }

                  if (!isValidDate) {
                    setHasValue(true);
                  }
                }}
                onClick={(e) => {
                  isMouseDown.current = false;
                  e.stopPropagation();
                  if (!isValidDate) {
                    setHasValue(true);
                  }
                }}
                onBlur={() => {
                  if (isValidDate) {
                    setHasValue(true);
                  } else {
                    setHasValue(false);
                  }
                }}
                className="relative"
              >
                <DatePicker
                  slots={{
                    openPickerIcon: () => (
                      <Icon
                        id="r1-c20"
                        width={25}
                        height={20}
                        color="#8F8F8F"
                      />
                    ),
                  }}
                  format="DD/MM/YYYY"
                  localeText={{
                    fieldMonthPlaceholder: () => (lang === "ar" ? "شهر" : "MM"),
                    fieldDayPlaceholder: () => (lang === "ar" ? "يوم" : "DD"),
                    fieldYearPlaceholder: () =>
                      lang === "ar" ? "سنة" : "YYYY",
                  }}
                  name={name}
                  inputRef={ref}
                  variant="outlined"
                  fullWidth
                  value={otherProps.value}
                  onChange={otherProps.onChange}
                  sx={{
                    width: "100%",
                    backgroundColor: inputBg,
                    "& fieldset": {
                      border: "none",
                    },
                    ...sx,
                  }}
                  className={`rounded-lg !shadow ${className}`}
                  {...otherProps}
                />
                <label
                  style={{
                    position: "absolute",
                    display: hasValue ? "none" : "flex",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#6b7280",
                    pointerEvents: "none",
                    zIndex: 1,
                    transition: "top 0.2s, transform 0.2s",
                    backgroundColor: inputBg,
                    width: "calc(100% - 150px)",
                    height: "40px",
                  }}
                  className={lang === "en" ? "left-[12px]" : "right-[12px]"}
                >
                  <Text
                    color="#a2a2a2"
                    weight={400}
                    fontType="Expo_bok"
                    className="my-auto !text-[12px] sm:!text-[16px]"
                  >
                    {placeholder}
                  </Text>
                </label>
              </div>
              <FormHelperText className="pt-2 rtl:text-right ltr:text-left">
                {helperText}
              </FormHelperText>
            </FormControl>
          </LocalizationProvider>
        </div>
      </div>
    );
  }
);

DateComponent.displayName = "DateComponent";

export default DateComponent;
